import React from "react";
import { FaRegArrowAltCircleUp } from "react-icons/fa";
import styled from "styled-components";
import { FormContainer } from "../../../../components/pages/CourseAuthor/FormElements";
import { config } from "../../../../config";
import {
  CustomFileInput,
  DownloadFileButton,
  ErrorHeader,
  Input,
  SetupButton,
} from "../../../Common/Components/FormComponents";
import { Section, SectionTitle } from "../../../Common/Styles/containers";
import { getDownloadAccountsUrl, getDownloadGroupsUrl, getDownloadMembersUrl } from "../helpers/export";
import { IErrorMessage, UploadType } from "../types";

const UploadIcon = styled(FaRegArrowAltCircleUp)`
  margin: 0 0.5rem;
`;

const Buttons = styled.div`
  margin: 1rem 0;
  width: 18rem;
`;

const SectionFilesImport = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

interface IOrgSettingsImport {
  orgId: string;
  accountId: string;
  uploadCsv: (file: File, settingsType: UploadType) => void;
  uploadError: IErrorMessage | null;
}

interface IFileInputs {
  clickRef: () => void;
  downloadUrl: () => string;
  inputName: string;
  ref: React.RefObject<HTMLInputElement>;
  requestSent: boolean;
  setFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileState: File | null;
  title: string;
  uploadFile: () => void;
}

const setInputFile = (e: React.ChangeEvent<HTMLInputElement>) =>
  e.target.files && e.target.files.length !== 0 ? e.target.files[0] : null;

const Import: React.FC<IOrgSettingsImport> = props => {
  const accountFileInput = React.useRef<HTMLInputElement>(null);
  const groupsFileInput = React.useRef<HTMLInputElement>(null);
  const membersFileInput = React.useRef<HTMLInputElement>(null);

  const [accountFile, setAccountFile] = React.useState<File | null>(null);
  const [groupsFile, setGroupsFile] = React.useState<File | null>(null);
  const [membersFile, setMembersFile] = React.useState<File | null>(null);

  const [accountSent, setAccountSent] = React.useState<boolean>(false);
  const [groupsSent, setGroupsSent] = React.useState<boolean>(false);
  const [membersSent, setMembersSent] = React.useState<boolean>(false);

  const root = config.saltcomplianceApiRoot;
  const { orgId, accountId } = props;

  const fileInputs: IFileInputs[] = [
    {
      clickRef: () => accountFileInput.current && accountFileInput.current.click(),
      downloadUrl: () => getDownloadAccountsUrl(root, orgId, accountId),
      fileState: accountFile,
      inputName: "importAccountsFile",
      ref: accountFileInput,
      requestSent: accountSent,
      setFile: (e: React.ChangeEvent<HTMLInputElement>) => setAccountFile(setInputFile(e)),
      title: "Accounts",
      uploadFile: () => {
        if (accountFile) {
          props.uploadCsv(accountFile, "accounts");
          setAccountSent(true);
        }
      },
    },
    {
      clickRef: () => groupsFileInput.current && groupsFileInput.current.click(),
      downloadUrl: () => getDownloadGroupsUrl(root, orgId, accountId),
      fileState: groupsFile,
      inputName: "importGroupsFile",
      ref: groupsFileInput,
      requestSent: groupsSent,
      setFile: (e: React.ChangeEvent<HTMLInputElement>) => setGroupsFile(setInputFile(e)),
      title: "Groups",
      uploadFile: () => {
        if (groupsFile) {
          props.uploadCsv(groupsFile, "groups");
          setGroupsSent(true);
        }
      },
    },
    {
      clickRef: () => membersFileInput.current && membersFileInput.current.click(),
      downloadUrl: () => getDownloadMembersUrl(root, orgId, accountId),
      fileState: membersFile,
      inputName: "importMembersFile",
      ref: membersFileInput,
      requestSent: membersSent,
      setFile: (e: React.ChangeEvent<HTMLInputElement>) => setMembersFile(setInputFile(e)),
      title: "Members",
      uploadFile: () => {
        if (membersFile) {
          props.uploadCsv(membersFile, "members");
          setMembersSent(true);
        }
      },
    },
  ];

  return (
    <SectionFilesImport>
      {fileInputs.map(item => (
        <FormContainer key={item.title}>
          <SectionTitle>{item.title}</SectionTitle>
          <DownloadFileButton
            text={`Download Existing ${item.title}`}
            margin={0}
            width={26}
            downloadUrl={item.downloadUrl}
          />
          <Input
            accept=".csv"
            type="file"
            name={item.inputName}
            onChange={item.setFile}
            ref={item.ref}
            style={{ display: "none" }}
          />
          <CustomFileInput onClickFunc={item.clickRef} file={item.fileState} />
          <Buttons>
            <SetupButton disabled={!item.fileState} onClick={item.uploadFile}>
              <UploadIcon /> Upload {item.title} File
            </SetupButton>
          </Buttons>
          {props.uploadError?.uploadType === item.title.toLowerCase() ? (
            <ErrorHeader>{props.uploadError.message}</ErrorHeader>
          ) : (
            item.requestSent && <label>Upload completed.</label>
          )}
        </FormContainer>
      ))}
    </SectionFilesImport>
  );
};

export default Import;
